import { z } from "zod";

import { BackendEntityType } from "./AttachmentModel";
import { baseCreateType } from "./CreateTypes";
import { documentFileResolver } from "./DataExports";
import { InvitationStatus } from "./Invitation";
import { mediaModelResolver } from "./MediaModel";
import { paginationTokenResolver } from "./Search";
import { userProfileResolver } from "./UserModel";

export enum WaiverFormat {
	file = "FILE",
	externalLink = "EXTERNAL_LINK"
}

export enum WaiverTypes {
	SOCCER_COMPETITION_PLAYER = "waiver.soccer.competition.player",
	SOCCER_COMPETITION_CLUB = "waiver.soccer.competition.club"
}

export const waiverFileResolver = z.object({
	id: z.string(),
	variants: mediaModelResolver.array()
});

export type WaiverFileModel = z.infer<typeof waiverFileResolver>;

export enum WaiverFilters {
	status = "status",
	playersSignatures = "playersSignatures"
}

export interface WaiverFilterModel {
	status?: InvitationStatus[];
	playersSignatures?: InvitationStatus[];
}

export const waiverResolver = baseCreateType.pick({ id: true }).extend({
	dueDate: z.string().nullable(),
	entityId: z.string(),
	entityType: z.nativeEnum(BackendEntityType),
	format: z.nativeEnum(WaiverFormat),
	link: z.string().nullable(),
	status: z.nativeEnum(InvitationStatus),
	title: z.string(),
	type: z.nativeEnum(WaiverTypes),
	file: documentFileResolver.nullable().or(z.string().nullable()),
	signatureStats: z
		.object({
			signed: z.number(),
			pending: z.number(),
			total: z.number()
		})
		.optional()
});

export type WaiverModel = z.infer<typeof waiverResolver>;

export const waiverSignatureResolver = baseCreateType.pick({ id: true }).extend({
	status: z.nativeEnum(InvitationStatus),
	person: userProfileResolver
		.pick({
			id: true,
			name: true,
			avatar: true
		})
		.extend({
			teams: z.array(baseCreateType.pick({ id: true, name: true, logo: true })).optional()
		})
		.optional(),
	waiver: waiverResolver
});

export type WaiverSignatureModel = z.infer<typeof waiverSignatureResolver>;

export const waiverSignaturesListResolver = waiverResolver.pick({ id: true, type: true }).extend({
	signatures: waiverSignatureResolver.omit({ waiver: true }).array(),
	nextToken: paginationTokenResolver
});

export type WaiverSignaturesListModel = z.infer<typeof waiverSignaturesListResolver>;
